/**
 * Per-tab audio mute for browser surfaces in the right panel.
 *
 * t3code mutes a desktop preview tab through its preview bridge; forge's
 * equivalent is the desktop shell's native browser view, which hosts the real
 * webContents. On web there is nothing to mute, so availability is answered
 * from the desktop bridge and the tab offers no mute control rather than one
 * that cannot work.
 *
 * The muted flag is session state on purpose: it lives with the running
 * session and dies with it, so a reload never shows a muted indicator for a
 * view the desktop has already forgotten.
 */
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { create } from "zustand";

import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";

const EMPTY_SURFACE_IDS: ReadonlySet<string> = new Set<string>();

interface PreviewAudioState {
  readonly mutedSurfaceIdsByThreadKey: Readonly<Record<string, ReadonlySet<string>>>;
  readonly setMuted: (input: {
    readonly threadKey: string;
    readonly surfaceId: string;
    readonly muted: boolean;
  }) => void;
}

const usePreviewAudioStore = create<PreviewAudioState>()((set) => ({
  mutedSurfaceIdsByThreadKey: {},
  setMuted: ({ threadKey, surfaceId, muted }) =>
    set((state) => {
      const current = state.mutedSurfaceIdsByThreadKey[threadKey] ?? EMPTY_SURFACE_IDS;
      if (current.has(surfaceId) === muted) return state;
      const next = new Set(current);
      if (muted) {
        next.add(surfaceId);
      } else {
        next.delete(surfaceId);
      }
      return {
        mutedSurfaceIdsByThreadKey: { ...state.mutedSurfaceIdsByThreadKey, [threadKey]: next },
      };
    }),
}));

/**
 * Whether the desktop native browser bridge is present; mute is desktop-only.
 */
function isPreviewAudioMuteAvailable(): boolean {
  return typeof window !== "undefined" && window.desktopBridge?.nativeBrowser !== undefined;
}

export interface RightPanelPreviewAudioControls {
  /** The strip's muted surface ids for this thread. */
  readonly mutedSurfaceIds: ReadonlySet<string>;
  readonly onToggleMute: (surfaceId: string) => void;
}

/**
 * The tab strip's mute controls for one thread, or null where there is no
 * native browser view to address.
 */
export function useRightPanelPreviewAudio(
  threadRef: ScopedThreadRef,
): RightPanelPreviewAudioControls | null {
  const available = isPreviewAudioMuteAvailable();
  const mutedSurfaceIds = usePreviewAudioStore(
    (state) => state.mutedSurfaceIdsByThreadKey[scopedThreadKey(threadRef)] ?? EMPTY_SURFACE_IDS,
  );
  if (!available) return null;
  return {
    mutedSurfaceIds,
    onToggleMute: (surfaceId) => {
      void togglePreviewAudioMute(threadRef, surfaceId);
    },
  };
}

/**
 * Toggles one preview tab's mute over the native browser bridge. The store
 * flag moves first so the indicator is not waiting on IPC, and reverts if
 * the command fails.
 */
async function togglePreviewAudioMute(
  threadRef: ScopedThreadRef,
  surfaceId: string,
): Promise<void> {
  const native = window.desktopBridge?.nativeBrowser;
  if (!native) return;

  // A preview surface's resource id IS the browser tab id the session
  // snapshot carries (`browser:${tabId}`).
  const tabId = surfaceId.startsWith("browser:") ? surfaceId.slice("browser:".length) : surfaceId;
  const browserState = selectBrowserThreadState(
    useBrowserStateStore.getState().browserStateByThreadKey,
    threadRef,
  );
  const session =
    browserState === null
      ? null
      : Object.values(browserState.sessionsByBrowserId).find(
          (candidate) =>
            candidate.activeTabId === tabId || candidate.tabs.some((tab) => tab.tabId === tabId),
        );
  if (!session) return;

  const threadKey = scopedThreadKey(threadRef);
  const currentlyMuted =
    usePreviewAudioStore.getState().mutedSurfaceIdsByThreadKey[threadKey]?.has(surfaceId) ?? false;
  const nextMuted = !currentlyMuted;
  // The flag moves first so the indicator does not wait on the IPC round
  // trip; a failed command reverts it, leaving the flag wrong for at most one
  // toggle.
  usePreviewAudioStore.getState().setMuted({ threadKey, surfaceId, muted: nextMuted });
  await native
    .command({
      browserId: session.browserId,
      sessionId: session.sessionId,
      tabId,
      command: { type: "setAudioMuted", muted: nextMuted },
    })
    .catch(() => {
      usePreviewAudioStore.getState().setMuted({ threadKey, surfaceId, muted: !nextMuted });
    });
}
